/**
 * Терминал внутри Telegram.
 *
 * Telegram умеет открывать веб-страницу в себе — мини-приложением. Для
 * человека это выглядит как приложение в мессенджере: нажал кнопку под
 * полем ввода, и терминал открылся, уже зная, кто пришёл.
 *
 * Отсюда три дела, и все три — про то, чтобы страница перестала быть
 * страницей:
 *
 *   вход       Telegram передаёт подписанную строку о том, кто открыл.
 *              Просить у него же подтвердить это в чате — просить
 *              дважды доказать одно и то же.
 *   вид        Тема берётся у Telegram: тёмное приложение с белым окном
 *              внутри выглядит чужим.
 *   поведение  Окно разворачивается на всю высоту и не закрывается
 *              случайным движением вниз посреди графика.
 *
 * Всё это включается, только когда мы правда внутри Telegram. В обычном
 * браузере модуль ничего не делает и ничего не требует.
 */

import { coreApi } from '../api/client.js';
import { currentSession, saveSession } from './session.js';

/**
 * То немногое, чем мы пользуемся из API Telegram.
 *
 * Свой узкий интерфейс вместо чужих типов: пакет с ними тянет за собой
 * зависимость ради десятка полей, а меняется он чаще, чем эти поля.
 */
interface TelegramInsets {
  readonly top: number;
  readonly bottom: number;
}

interface TelegramWebApp {
  readonly initData: string;
  readonly colorScheme?: 'light' | 'dark';
  readonly themeParams?: Record<string, string>;
  /** ios, android, tdesktop, macos, weba… */
  readonly platform?: string;
  readonly isFullscreen?: boolean;
  /** Край экрана (вырез, полоса жестов) и кнопки самого Telegram поверх окна на весь экран. */
  readonly safeAreaInset?: TelegramInsets;
  readonly contentSafeAreaInset?: TelegramInsets;
  ready(): void;
  expand(): void;
  isVersionAtLeast?(version: string): boolean;
  requestFullscreen?(): void;
  disableVerticalSwipes?(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  onEvent?(event: string, handler: () => void): void;
}

function webApp(): TelegramWebApp | null {
  const telegram = (globalThis as { Telegram?: { WebApp?: TelegramWebApp } }).Telegram;
  const app = telegram?.WebApp;
  if (!app) return null;

  /*
   * Пустая `initData` — это «скрипт есть, а Telegram нас не открывал».
   *
   * Скрипт грузится на всякой странице, поэтому сам по себе он ничего
   * не доказывает. Проверять его наличие значило бы считать Telegram
   * любой браузер: терминал разворачивал бы чужое окно и показывал
   * ожидание входа там, где никакого Telegram нет.
   */
  return typeof app.initData === 'string' && app.initData !== '' ? app : null;
}

/** Открыт ли терминал внутри Telegram. */
export function insideTelegram(): boolean {
  return webApp() !== null;
}

/**
 * Планшет: на нём Telegram открывает мини-приложение окном поверх чата,
 * и `expand` его не разворачивает. На телефоне окно и так во всю высоту,
 * а полный экран спрятал бы под кнопки Telegram ещё и строку состояния.
 */
function wantsFullscreen(app: TelegramWebApp): boolean {
  const mobile = app.platform === 'ios' || app.platform === 'android';
  const side = Math.min(globalThis.screen?.width ?? 0, globalThis.screen?.height ?? 0);
  return mobile && side >= 600 && app.isVersionAtLeast?.('8.0') === true;
}

/**
 * Отступы от краёв на весь экран: сверху — вырез и кнопки Telegram
 * («закрыть», «меню»), которые он рисует поверх окна; снизу — полоса
 * жестов. Не на весь экран — нули: окно Telegram и так за краями.
 */
function fitInsets(app: TelegramWebApp): void {
  const full = app.isFullscreen === true;
  const top = full ? (app.safeAreaInset?.top ?? 0) + (app.contentSafeAreaInset?.top ?? 0) : 0;
  const bottom = full ? (app.safeAreaInset?.bottom ?? 0) : 0;
  const root = document.documentElement;
  root.classList.toggle('tg-full', full);
  root.style.setProperty('--tg-top', `${top}px`);
  root.style.setProperty('--tg-bottom', `${bottom}px`);
}

let prepared = false;

/**
 * Приготовить окно: развернуть, на планшете — на весь экран, и убрать
 * случайное закрытие.
 *
 * Вертикальный свайп в Telegram закрывает мини-приложение. На графике
 * это движение — обычная прокрутка цены, и человек терял бы терминал по
 * десять раз за сеанс. Методы новые, поэтому вызываются осторожно: на
 * старых клиентах их просто нет.
 *
 * Делается при каждом открытии, а не только при первом входе: раньше
 * человек с уже сохранённым сеансом получал неразвёрнутое окно.
 */
export function prepareTelegramWindow(): void {
  const app = webApp();
  if (!app || prepared) return;
  prepared = true;

  app.ready();
  app.expand();
  app.disableVerticalSwipes?.();
  for (const event of ['fullscreenChanged', 'safeAreaChanged', 'contentSafeAreaChanged']) app.onEvent?.(event, () => fitInsets(app));
  if (wantsFullscreen(app)) {
    try {
      app.requestFullscreen?.();
    } catch {
      // Клиент не умеет — остаётся окном, как раньше.
    }
  }
  fitInsets(app);
}

/**
 * Тема Telegram, если мы внутри него.
 *
 * `null` — мы не в Telegram или он не сказал. Тогда решает настройка
 * профиля, как и раньше.
 */
export function telegramTheme(): 'light' | 'dark' | null {
  return webApp()?.colorScheme ?? null;
}

/**
 * Покрасить шапку и фон окна Telegram под нашу тему.
 *
 * Без этого над терминалом висит полоса чужого цвета: приложение
 * тёмное, окно светлое, и стык виден. Цвет берётся из тех же токенов,
 * что и вся страница, — второй набор цветов разошёлся бы с первым.
 */
export function paintTelegramChrome(): void {
  const app = webApp();
  if (!app) return;

  const styles = getComputedStyle(document.documentElement);
  const ground = styles.getPropertyValue('--ground').trim();
  if (!ground.startsWith('#')) return;

  app.setHeaderColor?.(ground);
  app.setBackgroundColor?.(ground);
}

/**
 * Войти по подписи Telegram.
 *
 * Возвращает `true`, если сеанс появился или уже был. Молчит при
 * неудаче намеренно: человек увидит обычный экран входа, а не ошибку,
 * которую ему нечем исправить.
 */
export async function signInFromTelegram(): Promise<boolean> {
  if (currentSession()) return true;

  const app = webApp();
  if (!app || app.initData === '') return false;

  try {
    const result = await coreApi.telegramWebAppLogin(app.initData);
    saveSession({ token: result.token, user: result.user });
    return true;
  } catch {
    /*
     * Причина не показывается.
     *
     * Их две: подпись не сошлась или устарела. Обе означают одно и то
     * же действие — открыть приложение заново, — а для подбирающего
     * разница между ними была бы подсказкой.
     */
    return false;
  }
}
